<script lang="ts">
  import { tick } from 'svelte'
  import ActorFollowers from '#activities/components/actor_followers.svelte'
  // @ts-expect-error The path is somehow not found?!?
  import { getUserFediverseAcct } from '#activities/lib/helpers'
  import app from '#app/app'
  import { feedsAPI } from '#app/lib/api/feeds'
  import Flash from '#app/lib/components/flash.svelte'
  import type { FlashState } from '#app/lib/components/flash.svelte'
  import { screen } from '#app/lib/components/stores/screen'
  import { icon } from '#app/lib/icons'
  import { imgSrc } from '#app/lib/image_source'
  import { onlyHeadMetadataRequired, type MetadataUpdate } from '#app/lib/metadata/update'
  import { onChange } from '#app/lib/svelte/svelte'
  import { treq } from '#app/lib/treq'
  import { userContent } from '#app/lib/user_content'
  import Modal from '#components/modal.svelte'
  import UserInventory from '#inventory/components/user_inventory.svelte'
  import type { FocusedSection, OnUnselectProfile, ProfileSection } from '#inventory/types/inventory_browser'
  import UsersListings from '#listings/components/users_listings.svelte'
  import type { OrderedCollection } from '#server/types/activity'
  import type { GroupId } from '#server/types/group'
  import { itemsWithoutShelfPathname } from '#shelves/components/lib/shelves'
  import type { SerializedShelf } from '#shelves/lib/shelves'
  import { i18n } from '#user/lib/i18n'
  import AdminInfo from '#users/components/admin_info.svelte'
  import ProfileNav from '#users/components/profile_nav.svelte'
  import UserProfileButtons from '#users/components/user_profile_buttons.svelte'
  import { setInventoryStats, type SerializedUser } from '../lib/users'

  let {
    user,
    shelf = $bindable(),
    profileSection = $bindable(),
    groupId,
    standalone = false,
    focusedSection,
    showShelfFollowers,
    showUserFollowers = $bindable(),
    onUnselectProfile,
  }: {
    user: SerializedUser
    shelf?: SerializedShelf | 'without-shelf'
    profileSection?: ProfileSection
    groupId?: GroupId
    standalone?: boolean
    focusedSection: FocusedSection
    showShelfFollowers?: boolean
    showUserFollowers?: boolean
    onUnselectProfile?: OnUnselectProfile
  } = $props()

  const userId = $derived(user._id)
  const username = $derived(user.username)
  const bio = $derived(user.bio)
  const picture = $derived(user.picture)
  const created = $derived(user.created)
  const fediversable = $derived(user.fediversable)

  let flash: FlashState = $state()
  let userProfileEl: HTMLElement = $state()
  let followersCount: number = $state()

  let itemsCount: number = $state()
  let shelvesCount: number = $state()

  $effect(() => {
    onChange(user, () => {
      setInventoryStats(user)
        .then(() => {
          ;({ itemsCount, shelvesCount } = user)
        })
        .catch(err => flash = err)
    })
  })

  async function onFocus () {
    if (!userProfileEl) await tick()
    shelf = null
    navigateToSection()
  }

  function navigateToSection () {
    // Let app.navigate scroll to the page top when UserProfile
    // is already at the top itself (standalone mode), to make the UsersHomeNav visible
    const pageSectionElement = standalone ? null : userProfileEl
    let pathname, title, rss
    if (showUserFollowers) return
    if (profileSection === 'inventory') {
      pathname = user.inventoryPathname
      title = `${username} - ${i18n('Inventory')}`
      rss = feedsAPI('user', userId)
    } else if (profileSection === 'listings') {
      pathname = user.listingsPathname
      title = `${username} - ${i18n('Lists')}`
    } else if (focusedSection.type === 'shelf') {
      if (shelf === 'without-shelf') {
        pathname = itemsWithoutShelfPathname
        title = `${username} - ${i18n('Items without shelf')}`
      } else {
        pathname = `/shelves/${shelf._id}`
        title = `${username} - ${shelf.name}`
      }
    } else {
      title = username
      pathname = user.pathname
      rss = feedsAPI('user', userId)
    }
    const metadata: MetadataUpdate = {
      title,
      description: bio,
      image: picture,
      rss,
      fediverseAcct: user.fediversable ? getUserFediverseAcct(user) : null,
    }
    app.navigate(pathname, { pageSectionElement, metadata })
  }

  const waitingForFollowersCount = $derived(fediversable ? getFollowersCount() : undefined)

  async function getFollowersCount () {
    if (onlyHeadMetadataRequired) return
    const res = await treq('activitypub', 'get', 'followers', { name: username }) as OrderedCollection
    followersCount = res.totalItems
  }

  function showFollowersModal () {
    showUserFollowers = true
    app.navigate(`/users/${username}/followers`)
  }

  function closeFollowersModal () {
    showUserFollowers = false
    navigateToSection()
  }

  $effect(() => {
    if (focusedSection.type === 'user') onFocus()
  })
</script>

<div class="full-user-profile">
  <div class="user-profile" bind:this={userProfileEl}>
    <div class="user-card">
      <div class="avatar-wrapper">
        <img class="avatar" src={imgSrc(picture, 150, 150)} alt="{username} avatar" />
      </div>
      <div class="info">
        <h2 class="respect-case">
          <span class="username">{username}</span>
          {#if $screen.isSmallerThan('$smaller-screen')}
            {#if !standalone}
              <button class="unselect-profile" onclick={onUnselectProfile}>{@html icon('times')}</button>
            {/if}
          {:else}
            <AdminInfo id={userId} {created} />
          {/if}
        </h2>
        <ul class="data">
          {#if itemsCount != null}
            <li class="inventory-length">
              <span>{@html icon('book')}{i18n('books')}</span>
              <span class="count">{itemsCount}</span>
            </li>
          {/if}
          {#if shelvesCount != null}
            <li class="show-shelves-list shelves-length">
              <span>{@html icon('server')}{i18n('shelves')}</span>
              <span class="count">{shelvesCount}</span>
            </li>
          {/if}
          {#if fediversable}
            {#await waitingForFollowersCount}
              {#if followersCount > 0}
                <li class="followers-count">
                  <a href="{username}/followers" onclick={showFollowersModal}>
                    <span>{@html icon('address-book')}{i18n('followers')}</span>
                    <span class="count">{followersCount}</span>
                  </a>
                </li>
              {/if}
            {/await}
          {/if}
        </ul>
        {#if $screen.isLargerThan('$smaller-screen')}
          {#if bio}
            <p class="bio-wrapper">{@html userContent(bio)}</p>
          {/if}
        {/if}
      </div>
    </div>

    {#if $screen.isSmallerThan('$smaller-screen')}
      {#if bio}
        <p class="bio-wrapper">{@html userContent(bio)}</p>
      {/if}
    {/if}
    <UserProfileButtons
      {user}
      bind:flash
      displayUnselectButton={!standalone}
      {onUnselectProfile}
    />
  </div>

  <Flash state={flash} />

  {#if !onlyHeadMetadataRequired}
    <ProfileNav {user} bind:profileSection {focusedSection} />

    {#if profileSection === 'listings'}
      <UsersListings usersIds={[ user._id ]} />
    {:else}
      <UserInventory
        {user}
        {groupId}
        {focusedSection}
        bind:selectedShelf={shelf}
        bind:flash
        {showShelfFollowers}
      />
    {/if}
  {/if}
</div>

{#if showUserFollowers}
  <Modal onCloseModal={closeFollowersModal}>
    <ActorFollowers
      actorName={username}
      actorLabel={username}
      standalone={true}
    />
  </Modal>
{/if}

<style lang="scss">
  @import "general/scss/utils";
  .full-user-profile{
    // Make sure it is possible to scroll to put the user profile at the top of the viewport
    min-height: 100vh;
  }
  h2{
    @include display-flex(row, baseline, flex-start);
    margin: 0;
  }
  .user-profile{
    background-color: #eee;
    @include display-flex(row);
    margin-block-end: 0.5rem;
    // Also include .unselect-profile in UserProfileButtons
    :global(.unselect-profile){
      width: 2.5rem;
      padding: 0.5rem 0;
      :global(.fa){
        @include shy;
        font-size: 2rem;
      }
    }
    .unselect-profile{
      align-self: flex-start;
      margin-inline-start: auto;
    }
  }
  .user-card{
    flex: 1;
    @include display-flex(row);
  }
  .avatar{
    background-color: #eee;
  }
  .info{
    position: relative;
  }
  .username{
    @include sans-serif;
    margin: 0;
  }
  .data{
    @include display-flex(row, flex-start);
    color: #666;
    margin-block-end: 0.5em;
    li{
      margin-inline-end: 1em;
    }
  }
  .inventory-length, .show-shelves-list, .followers-count{
    :global(.fa){
      padding-inline-end: 1.5em;
    }
    .count{
      padding-inline-start: 0.3em;
    }
  }
  .followers-count a{
    cursor: pointer;
    color: #666;
  }
  .bio-wrapper{
    @include radius;
    margin-block-end: 2em;
    max-width: 50em;
    overflow: auto;
  }

  /* Large screens */
  @media screen and (width >= $smaller-screen){
    .user-card{
      min-height: 9em;
    }
    .avatar-wrapper{
      width: 9em;
      flex: 0 0 auto;
    }
    .info{
      flex: 1 0 0;
      padding: 0.8em 1em 0;
    }
  }

  /* Smaller screens */
  @media screen and (width < $smaller-screen){
    .user-profile{
      @include display-flex(column, center, center);
    }
    .user-card{
      flex-wrap: wrap;
      align-self: stretch;
      margin: 0 0.5em;
    }
    .avatar-wrapper{
      flex: 1 0 0;
      max-width: 30%;
      min-width: 4em;
    }
    .info{
      flex: 2 0 0;
      min-width: min(10em, 90vw);
    }
    .username{
      margin: 0 0.5em;
      text-align: center;
    }
    .data{
      li{
        margin: 0 0 0.5em;
      }
      flex-direction: column;
      align-items: center;
    }
    .bio-wrapper{
      max-height: 10em;
      margin: 0.5em;
    }
  }
</style>
